import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { GameMode } from '@shared/chart';
import { loadModePref, saveModePref } from './modePref';

/**
 * Default mode used when neither the URL nor localStorage indicates a
 * preference. Pulse is the original game and remains the canonical
 * "first impression" until we have data showing Circles converts
 * better — at which point this is the one constant to flip.
 */
export const DEFAULT_MODE: GameMode = 'pulse';

/**
 * Maps the first path segment to a game mode. Returns null for paths
 * that aren't mode-prefixed (the mode picker at `/`, universal pages
 * like /profile or /calibrate, debug routes, legacy redirects). Used
 * by the TopNav and pages that need to know "which mode am I a part
 * of right now?".
 */
export function resolveModeFromPath(pathname: string): GameMode | null {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0];
  if (
    first === 'pulse' ||
    first === 'circles' ||
    first === 'piano' ||
    first === 'guitar' ||
    first === 'waves' ||
    first === 'jump'
  ) {
    return first;
  }
  return null;
}

/**
 * Builds a fully-qualified, mode-prefixed app path. Pass the rest as
 * either a single subpath (e.g. `'play/foo'`) or an empty string for
 * the mode home (`/pulse` or `/circles`). Centralising path
 * construction here means a future rename ("/pulse" → "/dash") only
 * touches one file.
 */
export function modePath(mode: GameMode, subpath: string = ''): string {
  const trimmed = subpath.replace(/^\/+/, '');
  return trimmed === '' ? `/${mode}` : `/${mode}/${trimmed}`;
}

/**
 * The mode the TopNav and other context-aware UI should treat as
 * "current". Resolution order:
 *   1. URL path (`/pulse/...` → 'pulse', `/circles/...` → 'circles')
 *   2. localStorage preference (so /profile remembers what you were
 *      playing and links Browse/Build/Home into that mode)
 *   3. {@link DEFAULT_MODE}
 *
 * Implemented as a hook (rather than a pure function) because the
 * stored preference can change in a different tab via the storage
 * event, and we want the active tab's nav to reflect the new mode
 * without a reload.
 */
export function useGameMode(): GameMode {
  const location = useLocation();
  const [storedMode, setStoredMode] = useState<GameMode | null>(() => loadModePref());

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'gd-mode-pref') {
        return;
      }
      setStoredMode(loadModePref());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const fromPath = resolveModeFromPath(location.pathname);
  // When the URL itself names a mode it always wins — keeping the
  // local cache fresh so a /profile visit later picks up where the
  // player just left off.
  useEffect(() => {
    if (fromPath && fromPath !== storedMode) {
      saveModePref(fromPath);
      setStoredMode(fromPath);
    }
  }, [fromPath, storedMode]);

  return fromPath ?? storedMode ?? DEFAULT_MODE;
}
